import React, { useEffect, useImperativeHandle, useRef, useState } from 'react'
import { InputProps } from './types'
import * as rawStyles from './styles.module.scss'
import { CheckIcon, EyeIcon, InfoIcon, NoEyeIcon } from '../icons'

const Input = React.forwardRef<HTMLInputElement, InputProps>(
    (
        {
            value,
            label,
            error = false,
            success = false,
            disabled = false,
            showStartIcon,
            message,
            successMessage,
            errorMessage,
            endIcon,
            errorIcon,
            successIcon,
            showPasswordIcon,
            hidePasswordIcon,
            testId,
            startIcon,
            showEndIcon,
            ...props
        },
        ref,
    ) => {
        const styles: any = rawStyles
        const inputRef = useRef<HTMLInputElement>(null)
        useImperativeHandle(ref, () => inputRef?.current as HTMLInputElement)

        const isInitialFilled = !!(value || props.defaultValue)
        const [isFilled, setIsFilled] = useState(isInitialFilled)
        const [isPasswordVisible, setIsPasswordVisible] = useState(false)

        const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
            const newValue = event.target.value.trim()
            const newIsFilled = newValue.length > 0
            setIsFilled(newIsFilled)
            if (props.onChange) props.onChange(event)
        }

        const handleAnimationStart = (event: React.AnimationEvent<HTMLInputElement>) => {
            const isFilled = event.animationName.startsWith('onAutoFillStart')
            setIsFilled(isFilled)
        }

        const handlePasswordIconClick = (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
            if (!inputRef.current) return
            e.stopPropagation()
            setIsPasswordVisible(!isPasswordVisible)
        }

        const getEndIconsToDisplay = () => {
            const icons = []
            if (endIcon && showEndIcon) icons.push(endIcon)
            if (success)
                icons.push(
                    <div
                        key='success_icon'
                        aria-label='success icon'
                        className={styles.success_icon}>
                        {successIcon ? successIcon : <CheckIcon />}
                    </div>,
                )
            if (error)
                icons.push(
                    <div key='error_icon' aria-label='error icon' className={styles.error_icon}>
                        {errorIcon ? (
                            errorIcon
                        ) : (
                            <div className={styles.error_wrapper}>
                                <InfoIcon />
                            </div>
                        )}
                    </div>,
                )
            if (props.type === 'password') {
                const pwdIcon = showPasswordIcon ? showPasswordIcon : <EyeIcon />
                const hidePwdIcon = hidePasswordIcon ? hidePasswordIcon : <NoEyeIcon />
                icons.push(
                    <button
                        key='password-button'
                        aria-label='password button'
                        className={styles.password_icon}
                        style={{ zIndex: 1, cursor: 'pointer' }}
                        onMouseDown={(e) => e.preventDefault()}
                        onClick={handlePasswordIconClick}>
                        {isPasswordVisible ? pwdIcon : hidePwdIcon}
                    </button>,
                )
            }
            return icons
        }

        const getStatusMessageToDisplay = (): string | undefined => {
            if (success && successMessage && successMessage.length > 0) return successMessage
            if (error && errorMessage && errorMessage.length > 0) return errorMessage
            return undefined
        }

        useEffect(() => {
            if (inputRef.current && props.type === 'password') {
                if (isPasswordVisible) {
                    inputRef.current.type = 'text'
                } else {
                    inputRef.current.type = 'password'
                }
            }
        }, [isPasswordVisible, inputRef.current, props.type])

        const isStartIcon = (startIcon && showStartIcon) || false

        return (
            <div
                className={styles.b_input}
                data-is-error={error}
                data-is-success={success}
                data-is-disabled={disabled}
                data-is-filled={isFilled}
                data-is-end-icon={getEndIconsToDisplay().length > 0}
                data-is-start-icon={isStartIcon}
                data-testid={testId && testId}>
                <div className={styles.input_box}>
                    {startIcon && showStartIcon && (
                        <div className={styles.start_icon_container}>{startIcon}</div>
                    )}
                    <div className={styles.input_container}>
                        <label aria-hidden={true} className={styles.label} htmlFor={props.name}>
                            {label}
                        </label>
                        <input
                            {...props}
                            value={value}
                            onChange={handleChange}
                            ref={inputRef}
                            disabled={disabled}
                            onAnimationStart={handleAnimationStart}
                            id={props.name}
                        />
                    </div>
                    {getEndIconsToDisplay().length > 0 && (
                        <div className={styles.end_icons_container}>{getEndIconsToDisplay()}</div>
                    )}
                </div>
                {getStatusMessageToDisplay() !== undefined && (
                    <div className={styles.status_message}>{getStatusMessageToDisplay()}</div>
                )}
                {message && <div className={styles.message}>{message}</div>}
            </div>
        )
    },
)
Input.displayName = 'Input'

export default Input
